import { ChronoUnit, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { toLocalDateTime } from "../../../../shared/utils/date-utils";
import { ExtendedContactDetails, Field, FormState } from "./extended-contact-details.types";

export const FIELDS: Field[] = [
  { key: "birthCountryPlace", label: "Birth Country/Place", type: "text" },
  {
    key: "lookingForLongPermanentCases",
    label: "Are you looking for long permanent cases",
    type: "text",
  },
  { key: "cdpapToPcaSwitch", label: "CDPAP to PCA switch", type: "boolean" },
  { key: "commentForTeam", label: "Comment for team", type: "textarea" },
  {
    key: "experienceWithBedboundPatient",
    label: "Experience with a bedbound patient",
    type: "boolean",
  },
  { key: "dohFormSection3Complete", label: "DOH Form Section 3 Complete", type: "datetime" },
  { key: "eVerifySubmissionDate", label: "E-Verify Submission Date", type: "date" },
  {
    key: "experienceWithWalkingAssistanceNeed",
    label: "Experience with walking assistance need",
    type: "boolean",
  },
  {
    key: "experienceWorkingWithShabbatObserver",
    label: "Experience working with a Shabbat observer",
    type: "boolean",
  },
  {
    key: "experienceWorkingAtKosherHome",
    label: "Experience working at a Kosher home",
    type: "boolean",
  },
  {
    key: "experienceWorkingWithHalalCulture",
    label: "Experience working with the halal culture",
    type: "boolean",
  },
  { key: "eyeColor", label: "Eye Color", type: "text" },
  { key: "hairColor", label: "Hair Color", type: "text" },
  { key: "height", label: "Height", type: "text" },
  { key: "isCaregiverSmoking", label: "Is caregiver smoking", type: "boolean" },
];

export function toDisplayValue(
  details: ExtendedContactDetails | null,
  field: Field
): string | null {
  if (details === null) {
    return null;
  }

  switch (field.type) {
    case "text":
    case "textarea":
      return details[field.key];
    case "boolean": {
      const value = details[field.key];
      return value === null ? null : value ? "Yes" : "No";
    }
    case "date": {
      const value = details[field.key];
      return value === null ? null : dateFormatter.toDate(value);
    }
    case "datetime": {
      const value = details[field.key];
      return value === null ? null : dateFormatter.toDateTime(value);
    }
  }
}

export function toFormState(details: ExtendedContactDetails | null): FormState {
  const form = {} as FormState;
  for (const field of FIELDS) {
    form[field.key] = toInputValue(details, field);
  }
  return form;
}

export function toRequestBody(form: FormState): ExtendedContactDetails {
  const body = {} as ExtendedContactDetails;
  for (const field of FIELDS) {
    const raw = form[field.key].trim();
    switch (field.type) {
      case "text":
      case "textarea":
        body[field.key] = raw === "" ? null : raw;
        break;
      case "boolean":
        body[field.key] = raw === "" ? null : raw === "yes";
        break;
      case "date":
        body[field.key] = raw === "" ? null : LocalDate.parse(raw);
        break;
      case "datetime":
        body[field.key] =
          raw === "" ? null : LocalDateTime.parse(raw).atZone(ZoneId.systemDefault()).toInstant();
        break;
    }
  }
  return body;
}

function toInputValue(details: ExtendedContactDetails | null, field: Field): string {
  if (details === null) {
    return "";
  }

  switch (field.type) {
    case "text":
    case "textarea":
      return details[field.key] ?? "";
    case "boolean": {
      const value = details[field.key];
      return value === null ? "" : value ? "yes" : "no";
    }
    case "date":
      return details[field.key]?.toString() ?? "";
    case "datetime": {
      const value = details[field.key];
      return value === null
        ? ""
        : toLocalDateTime(value).truncatedTo(ChronoUnit.MINUTES).toString();
    }
  }
}
